/**
 * Pure helpers for the "Read in <your language>" suggestion shown to
 * non-English visitors on English (un-prefixed) pages. Kept in a plain .ts
 * module (not the 'use client' component) so they're unit-testable and safe to
 * import anywhere.
 *
 * Why it exists: we opt out of Chrome's AUTOMATIC page translation
 * (<meta name="google" content="notranslate"> in src/app/layout.tsx — Chrome
 * iOS's injected Translate script overflows the stack after soft navigations).
 * This points those readers at our own translations instead.
 */
import { locales, defaultLocale, type Locale } from './config'

/**
 * Banner copy, per target locale. Static (not next-intl messages) because an
 * English page only ships the `en` message bundle to the client.
 */
export const SUGGESTION_COPY: Record<Exclude<Locale, 'en'>, { cta: string; dismiss: string }> = {
  es: { cta: 'Leer en español', dismiss: 'Cerrar' },
  fr: { cta: 'Lire en français', dismiss: 'Fermer' },
  de: { cta: 'Auf Deutsch lesen', dismiss: 'Schließen' },
  pt: { cta: 'Ler em português', dismiss: 'Fechar' },
  ja: { cta: '日本語で読む', dismiss: '閉じる' },
  zh: { cta: '阅读中文版', dismiss: '关闭' },
  ko: { cta: '한국어로 읽기', dismiss: '닫기' },
  ru: { cta: 'Читать на русском', dismiss: 'Закрыть' },
  ar: { cta: 'اقرأ بالعربية', dismiss: 'إغلاق' },
}

/**
 * The first supported non-English locale in the visitor's preference list, or
 * null. Walks `navigator.languages` in order and stops at English — someone who
 * prefers English (even after another language) gets no suggestion. Languages
 * we don't serve are skipped (`['it', 'fr']` → fr). Matching is on the primary
 * subtag, so pt-BR → pt and zh-TW → zh (our Chinese is Simplified, still the
 * closest we have).
 */
export function pickSuggestedLocale(languages: readonly string[] | undefined | null): Exclude<Locale, 'en'> | null {
  for (const tag of languages ?? []) {
    const primary = String(tag).toLowerCase().split(/[-_]/)[0]
    if (!primary) continue
    if (primary === defaultLocale) return null
    if ((locales as readonly string[]).includes(primary)) return primary as Exclude<Locale, 'en'>
  }
  return null
}

/** True when `pathname` already carries a non-default locale prefix (/fr, /fr/…). */
export function hasLocalePrefix(pathname: string): boolean {
  const m = pathname.match(/^\/([a-z]{2})(?=\/|$)/)
  return !!m && m[1] !== defaultLocale && (locales as readonly string[]).includes(m[1])
}

/** `/tutorials/foo` + `?x=1` → `/fr/tutorials/foo?x=1`; `/` → `/fr`. */
export function localizedHref(pathname: string, search: string, locale: Exclude<Locale, 'en'>): string {
  const path = pathname === '/' ? '' : pathname
  return `/${locale}${path}${search}`
}
